<template>
    <v-container fluid class="mt-6">
        <v-row justify="center">
            <v-col cols="12" v-if="error">
                <dashboard-error></dashboard-error>
            </v-col>
            <v-col cols="12" v-else>
                <div class="d-flex align-center">
                    <v-btn color="primary" @click="csvExport()" :loading="csvExporting">Export to CSV</v-btn>
                    <v-spacer></v-spacer>
                    <v-menu offset-y :close-on-content-click="false">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn v-bind="attrs" v-on="on" class="mr-2" color="white">
                                Columns
                                <v-icon right>mdi-chevron-down</v-icon>
                            </v-btn>
                        </template>
                        <v-list>
                            <v-list-item-group v-model="selectedHeaders" multiple>
                                <v-list-item v-for="column in headers" :key="column.value" :value="column">
                                    <template v-slot:default="{ active }">
                                        <v-list-item-action>
                                            <v-checkbox :input-value="active"></v-checkbox>
                                        </v-list-item-action>
                                        <v-list-item-content>
                                            <v-list-item-title>{{ column.text }}</v-list-item-title>
                                        </v-list-item-content>
                                    </template>
                                </v-list-item>
                            </v-list-item-group>
                        </v-list>
                    </v-menu>
                    <v-text-field
                        v-model="search"
                        dense
                        solo
                        flat
                        hide-details
                        clearable
                        prepend-inner-icon="mdi-magnify"
                        :placeholder="$route.params.oid ? 'Search for a space...' : 'Search for an organization...'">
                        <template v-slot:append>
                            <v-menu offset-y :close-on-content-click="false">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn v-bind="attrs" v-on="on" small class="mr-n2">
                                        <v-icon left>mdi-tune-variant</v-icon>
                                        <span>Filters</span>
                                    </v-btn>
                                </template>
                                <v-list nav dense>
                                    <v-sheet v-for="filter in filters" :key="filter.filter_id" rounded class="py-2 px-4 my-1">
                                        <v-switch v-model="selectedFilters" :value="filter" inset dense hide-details class="pa-0 ma-0">
                                            <template v-slot:label>
                                                <div class="d-flex align-center">
                                                    <span>{{ filter.name }}</span>
                                                    <v-tooltip v-if="filter.description" right>
                                                        <template v-slot:activator="{ on, attrs }">
                                                            <v-icon small tabindex="0" class="ml-2" v-bind="attrs" v-on="on" :aria-label="filter.description">
                                                                mdi-information-outline
                                                            </v-icon>
                                                        </template>
                                                        <span>{{ filter.description }}</span>
                                                    </v-tooltip>
                                                </div>
                                            </template>
                                        </v-switch>
                                    </v-sheet>
                                </v-list>
                            </v-menu>
                        </template>
                    </v-text-field>
                </div>
                <v-data-table
                    :headers="filteredHeaders"
                    :items="inventory"
                    :item-key="$route.params.oid ? 'sid' : 'oid'"
                    :options.sync="options"
                    :server-items-length="inventorySize"
                    :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                    :loading="fetchingInitInventory || fetchingInventory || fetchingInventorySize"
                    @click:row="clickRow"
                    :expanded.sync="expanded"
                    show-expand
                    class="inventory-table mt-4">
                    <template v-slot:top="{ options, pagination, updateOptions }">
                        <div class="d-flex align-center flex-wrap">
                            <v-spacer></v-spacer>
                            <v-data-footer
                                :pagination="pagination"
                                :options="options"
                                @update:options="updateOptions"
                                :itemsPerPageOptions="itemsPerPageOptions"
                                items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                                class="no-border" />
                        </div>
                    </template>
                    <template v-slot:[`item.ncu_usage`]="{ item }">
                        {{ formatNcuUsage(item.ncu_usage) }}
                    </template>
                    <template v-slot:expanded-item="{ headers, item }">
                        <td :colspan="headers.length">
                            <inventory-dropdown :oid="item?.oid" :sid="item?.sid"></inventory-dropdown>
                        </td>
                    </template>
                    <template v-slot:[`item.actions`]="{ item }">
                        <v-menu v-if="item.space_admins === 0 && !item.delete_timestamp" left offset-x nudge-top="10">
                            <template v-slot:activator="{ on }">
                                <v-btn v-on="on" text color="secondary" small>
                                    <v-icon>more_horiz</v-icon>
                                </v-btn>
                            </template>
                            <v-list nav dense>
                                <v-list-item @click.stop="deleteSpace(item.sid)">
                                    <v-list-item-icon>
                                        <v-progress-circular v-if="deletingSpace === item.sid" size="18" width="2" indeterminate color="error" />
                                        <v-icon v-else color="error">mdi-trash-can-outline</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-title class="error--text">Delete</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import dashboard from '@/mixins/dashboard'
import { pick } from 'lodash'
import jsonexport from 'jsonexport'
import { bytesToGB } from '@/utils'
import { createBackendErrorHandler } from '@/utils/errorMessages'
import * as integrationsErrors from '@/modules/user/errors/integrations'
import * as permissionsErrors from '@/utils/errors/permissions'
import * as resourcePoolErrors from '@/modules/user/errors/resourcePools'

const { getBackendErrorMessage } = createBackendErrorHandler(integrationsErrors, permissionsErrors, resourcePoolErrors)

const InventoryDropdown = () => import('@/modules/user/components/InventoryDropdown.vue')

export default {
    name: 'ResourcePoolInventory',
    mixins: [dashboard],
    components: { InventoryDropdown },
    data() {
        return {
            search: '',
            searchTimeout: null,
            headers: [],
            selectedHeaders: [],
            inventory: [],
            inventorySize: 0,
            precision: this.$appConfig?.VUE_APP_DASHBOARD_PRECISION,
            fetchingInventory: false,
            fetchingInventorySize: false,
            fetchingInitInventory: false,
            options: null,
            itemsPerPageOptions: [],
            filters: [],
            selectedFilters: [],
            error: null,
            expanded: [],
            csvExporting: false,
            deletingSpace: null,
            currentItem: null
        }
    },
    computed: {
        filteredHeaders() {
            return this.headers.filter(header => this.selectedHeaders.includes(header))
        },
        apiUrl() {
            if (!this.$route.params.rpid) return ''
            let url = `/resource/${this.$route.params.rpid}`
            if (this.$route.params.oid) url += `/org/${this.$route.params.oid}`
            return url
        },
        allDataFetched() {
            return !this.fetchingInitInventory && !this.fetchingInventory && !this.fetchingInventorySize
        },
        allMetaFetched() {
            return !this.fetchingInitInventory && !this.fetchingInventorySize
        }
    },
    beforeDestroy() {
        clearTimeout(this.searchTimeout)
    },
    watch: {
        search() {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                if (!this.options) return
                this.resetPagination()
                this.fetchInventorySize()
            }, 300)
        },
        options: {
            handler(options) {
                if (this.allMetaFetched) {
                    this.fetchInventory()
                }
            },
            deep: true
        },
        selectedHeaders() {
            if (this.$route.params.oid) {
                this.filteredHeaders.push({ text: 'Actions', value: 'actions', align: 'right', sortable: false })
            }
            this.filteredHeaders.push({ text: '', value: 'data-table-expand', align: 'right' })
        },
        selectedFilters() {
            this.selectedFilters.forEach(filter => {
                filter.column_requirements.forEach(columnId => {
                    if (!this.selectedHeaders.some(header => header.value === columnId)) {
                        this.selectedHeaders.push(this.headers.find(header => header.value === columnId))
                    }
                })
            })
            this.resetPagination()
            this.fetchInventorySize()
        },
        apiUrl: {
            handler() {
                this.initInventory()
            },
            immediate: true
        },
        allDataFetched(fetched) {
            if (!fetched) return
            this.$emit('fetching-charts', false)
        }
    },
    methods: {
        formatNcuUsage(value) {
            if (value === null || value === undefined) return ''
            return this.roundDataItem(value).toFixed(this.precision)
        },
        formatInventoryData(inventoryData) {
            return inventoryData.map(row => {
                this.headers.forEach(header => {
                    switch (header.format) {
                        case 'datetime':
                            row[header.value] = this.$options.filters.dateTimeToHuman(row[header.value])
                            break
                        case 'percentage':
                            row[header.value] = this.percentage(row[header.value])
                            break
                        case 'byte':
                            row[header.value] = `${bytesToGB(row[header.value])} GB`
                            break
                        case 'seconds':
                            if (row[header.value] !== null) {
                                row[header.value] = `${row[header.value].toLocaleString()}s`
                            }
                            break
                    }
                })
                return row
            })
        },
        formatInventoryDataForCSV(inventoryData) {
            return inventoryData.map(row => {
                this.headers.forEach(header => {
                    switch (header.format) {
                        case 'datetime':
                            row[header.value] = this.$options.filters.dateTimeToHuman(row[header.value])
                            break
                        case 'byte':
                            row[header.value] = bytesToGB(row[header.value])
                            break
                    }
                })
                return row
            })
        },
        percentage(number) {
            if (typeof number !== 'number') return
            const multiplied = number * 100
            if (number > 0 && multiplied < 0.01) {
                return `0.01%`
            }
            return `${multiplied.toFixed(2)}%`
        },
        normalizedColumnName(columnName = '') {
            return columnName.trim().toLowerCase().replace(/\s+/g, '_')
        },
        downloadBlob(content, filename, contentType) {
            const blob = new Blob([content], { type: contentType })
            const url = URL.createObjectURL(blob)

            // Create a link to download it
            const pom = document.createElement('a')
            pom.href = url
            pom.setAttribute('download', filename)
            pom.click()
        },
        queryParams({ all = false } = {}) {
            const { sortBy, sortDesc, page, itemsPerPage } = this.options
            const query = {
                page: all ? 1 : page,
                per_page: all ? -1 : itemsPerPage
            }
            if (sortBy.length > 0) {
                query.sort_by = sortBy
                query.sort_desc = sortDesc
            }
            const queryParams = new URLSearchParams(query)
            const search = (this.search || '').trim()
            if (search) queryParams.set('search', search)
            if (this.selectedFilters.length > 0) {
                this.selectedFilters.forEach(filter => {
                    queryParams.append('filter', filter.filter_id)
                })
            }
            return queryParams.toString()
        },
        async csvExport() {
            this.csvExporting = true
            try {
                const { data } = await this.$axios.get(`${this.apiUrl}/inventory?${this.queryParams({ all: true })}`)
                this.formatInventoryDataForCSV(data)
                const filteredHeaderIds = this.filteredHeaders.map(header => header.value)
                const contacts = await Promise.all(
                    data.map(row => {
                        const contactUrl = this.$route.params.oid
                            ? `/resource/${this.$route.params.rpid}/space/${row.sid}/contacts`
                            : `/resource/${this.$route.params.rpid}/org/${row.oid}/contacts`
                        return this.$axios.get(contactUrl)
                    })
                )
                const contactColumnTitle = this.$route.params.oid ? 'Space admins' : 'Managers'
                const csvData = data.map((row, index) => {
                    return {
                        ...pick(row, filteredHeaderIds),
                        [contactColumnTitle]: contacts[index].data.map(contact => `${contact.full_name} <${contact.email}>`).join('; ')
                    }
                })
                // Use human readable titles for the columns:
                csvData.forEach(row => {
                    for (const key in row) {
                        const header = this.filteredHeaders.find(header => header.value === key)
                        if (!header) continue
                        let title = ''
                        switch (header.format) {
                            case 'byte':
                                title = header.text + ' [GB]'
                                break
                            case 'seconds':
                                title = header.text + ' [seconds]'
                                break
                            default:
                                title = header.text
                                break
                        }
                        delete Object.assign(row, { [title]: row[key] })[key]
                    }
                })

                const self = this
                jsonexport(csvData, {}, function (err, csv) {
                    if (err) return
                    self.downloadBlob(csv, 'export.csv', 'text/csv;charset=utf-8;')
                })
            } catch (error) {
                const snackBarText = getBackendErrorMessage(error, 'Failed to export CSV')
                if (snackBarText) {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText,
                        snackBarTimeout: 5000,
                        snackBarIcon: 'error'
                    })
                }
            }
            this.csvExporting = false
        },
        async deleteSpace(sid) {
            this.deletingSpace = sid
            try {
                await this.$axios.delete(`/resource/${this.$route.params.rpid}/space/${sid}`)
                this.fetchInventorySize()
            } catch (error) {
                const snackBarText = getBackendErrorMessage(error, 'Failed to delete space.')
                if (snackBarText) {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText,
                        snackBarTimeout: 5000,
                        snackBarIcon: 'error'
                    })
                }
            }
            this.deletingSpace = null
        },
        clickRow(item, event) {
            const itemKey = this.rowKey(item)
            if (event.isExpanded) {
                this.expanded = this.expanded.filter(expandedItem => this.rowKey(expandedItem) !== itemKey)
            } else {
                this.expanded.push(item)
            }
        },
        rowKey(item) {
            return this.$route.params.oid ? item.sid : item.oid
        },
        async initInventory() {
            this.fetchingInitInventory = true
            this.$emit('fetching-charts', true)
            try {
                const { data } = await this.$axios.get(`${this.apiUrl}/inventory?metadata=true&init=true`)
                this.inventorySize = data.count
                this.itemsPerPageOptions = data.items_per_page
                this.itemsPerPageOptions.push(-1)
                this.filters = data.filters
                // check for default filters
                const defaultFilters = this.filters.filter(filter => filter.default)
                this.selectedFilters = defaultFilters.length > 0 ? defaultFilters : []

                this.options = {
                    page: 1,
                    itemsPerPage: this.itemsPerPageOptions[0],
                    sortBy: [],
                    sortDesc: []
                }

                this.headers = data.columns.map(column => {
                    const normalizedId = this.normalizedColumnName(column.column_id)
                    const isNcuUsageToLimit = normalizedId === 'ncu_usage_to_limit' || normalizedId === 'ncu_usage_percent'
                    return {
                        text: column.name,
                        value: column.column_id,
                        visible: column.visible,
                        format: isNcuUsageToLimit ? 'percentage' : column.format
                    }
                })

                this.selectedHeaders = this.headers.filter(header => header.visible)
                await this.$nextTick()
            } catch (error) {
                this.error = error
            } finally {
                this.fetchingInitInventory = false
            }
        },
        async fetchInventorySize() {
            this.fetchingInventorySize = true
            this.$emit('fetching-charts', true)
            try {
                const { data } = await this.$axios.get(`${this.apiUrl}/inventory?metadata=true&${this.queryParams()}`)
                this.inventorySize = data.count
                this.fetchInventory()
            } catch (error) {
                this.error = error
            }
            this.fetchingInventorySize = false
        },
        async fetchInventory() {
            this.fetchingInventory = true
            this.$emit('fetching-charts', true)
            try {
                const { data } = await this.$axios.get(`${this.apiUrl}/inventory?${this.queryParams()}`)
                this.inventory = this.formatInventoryData(data)
            } catch (error) {
                this.error = error
            } finally {
                this.fetchingInventory = false
            }
        },
        resetPagination() {
            this.options.page = 1
            this.options.itemsPerPage = this.itemsPerPageOptions[0]
            this.options.sortBy = []
            this.options.sortDesc = []
        }
    }
}
</script>

<style scoped lang="scss">
@import '@/sass/dashboard.scss';
::v-deep .inventory-table {
    tr:not(.v-data-table__expanded__content) {
        cursor: pointer;
    }
}
</style>
